import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { FC, SyntheticEvent, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useReportsContext } from "@ryde/redux";
import { hasKeys, Log, NO_VALUE_DISPLAY } from "@ryde/shared";

import {
  IFlexGropingItem,
  IFlexGroupingPanelForm,
} from "./flex-grouping.models";

export const FlexGroupingFields: FC = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<IFlexGroupingPanelForm>();
  const { reportTemplate } = useReportsContext();
  const availableGroupings: IFlexGropingItem[] = useMemo(() => {
    if (
      !reportTemplate?.flexGroupingColumns ||
      !hasKeys(reportTemplate.flexGroupingColumns)
    ) {
      return [];
    }

    return Object.entries(reportTemplate.flexGroupingColumns).map(
      ([columnKey, columnObject]) => {
        return {
          value: columnKey,
          label: t(columnObject.columnTitleTranslationKey),
        };
      },
    );
  }, [reportTemplate?.flexGroupingColumns, t]);

  const optionsNormalized: string[] = useMemo(() => {
    return availableGroupings.map(x => x.value);
  }, [availableGroupings]);

  const onChangeDropdown = useEvent(
    (e: SyntheticEvent<Element, Event>, newValue: string[]): void => {
      Log.debug("Dropdown template value changed to:", newValue);
      setValue("groupingParams", newValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const getOptionLabel = useCallback(
    (option: string) => {
      return (
        availableGroupings.find(x => x.value === option)?.label ||
        NO_VALUE_DISPLAY
      );
    },
    [availableGroupings],
  );

  return (
    <Controller
      control={control}
      name={"groupingParams"}
      render={({
        field: { value: formValue, onChange, ...restField },
        fieldState: { error },
      }) => (
        <FormControl fullWidth error={!!error}>
          <Autocomplete
            {...restField}
            multiple
            size="small"
            value={formValue}
            options={optionsNormalized}
            getOptionLabel={getOptionLabel}
            onChange={(event, data) => {
              onChangeDropdown(event, data);
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label={t("report.formLabel.groupingParams")}
              />
            )}
          />
          {error && (
            <FormHelperText data-e2e="dropdown-bound-template-error">
              {error.message}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
